
// ─── Response Types ────────────────────────────────────────────────────────────

export interface ScrapeResult {
  status: string;
  donor_id?: string;
  linkedin_id?: string;
  linkedin_url?: string;
  saved?: boolean;
  linkedin_info: Record<string, unknown>;
  raw_linkedin_info: Record<string, unknown>;
}

export interface BulkScrapeResult {
  status: string;
  queued: number;
  message: string;
}

export type ScrapeEventMessage = {
  type: 'progress' | 'log' | 'success' | 'error' | 'completed';
  url?: string;
  message?: string;
  processed?: number;
  total?: number;
  result?: ScrapeResult;
};

// ─── Service ───────────────────────────────────────────────────────────────────

const getHeaders = (): Record<string, string> => {
  const key = localStorage.getItem('parallel_api_key');
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (key) {
    headers['X-Parallel-Api-Key'] = key;
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

/** Shared stream handler for SSE */
const handleSseStream = async (url: string, onMessage: (msg: ScrapeEventMessage) => void) => {
  const fullUrl = import.meta.env.DEV ? `/api${url}` : `/api${url}`;
  const response = await fetch(fullUrl, {
    method: 'POST',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to start scrape: ${response.statusText}`);
  }

  if (!response.body) {
    throw new Error('No readable stream returned by the server.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');

  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    
    // Process full lines
    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // Keep incomplete line in buffer

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const dataStr = line.replace('data: ', '').trim();
        if (dataStr) {
          try {
            const data = JSON.parse(dataStr) as ScrapeEventMessage;
            onMessage(data);
          } catch (e) {
            console.error('Failed to parse SSE JSON:', e, dataStr);
          }
        }
      }
    }
  }
};

export const linkedinService = {
  /**
   * Scrape a LinkedIn profile by the donor's internal donor_id.
   * The backend reads source_info.linked_in from the profile document.
   * Returns a promise that resolves with the final ScrapeResult.
   */
  scrapeByDonorId: (donorId: string, onProgress?: (msg: ScrapeEventMessage) => void): Promise<ScrapeResult> =>
    new Promise((resolve, reject) => {
      let finalResult: ScrapeResult | undefined;
      handleSseStream(`/v1/linkedin/scrape/donor/${donorId}`, (msg) => {
        if (onProgress) onProgress(msg);
        if (msg.type === 'completed') {
          finalResult = msg.result;
        } else if (msg.type === 'error' && !finalResult) {
          // Keep errors for logging, but we'll reject if it finishes without success
        }
      })
        .then(() => {
          if (finalResult) resolve(finalResult);
          else reject(new Error('Scrape completed without returning a result.'));
        })
        .catch(reject);
    }),

  /**
   * Scrape a LinkedIn profile directly by slug or full URL.
   * Matches the v2__profiles document via source_info.linked_in.
   */
  scrapeByLinkedinId: (linkedinId: string, onProgress?: (msg: ScrapeEventMessage) => void): Promise<ScrapeResult> =>
    new Promise((resolve, reject) => {
      let finalResult: ScrapeResult | undefined;
      handleSseStream(`/v1/linkedin/scrape/linkedin/${encodeURIComponent(linkedinId)}`, (msg) => {
        if (onProgress) onProgress(msg);
        if (msg.type === 'completed') {
          finalResult = msg.result;
        }
      })
        .then(() => {
          if (finalResult) resolve(finalResult);
          else reject(new Error('Scrape completed without returning a result.'));
        })
        .catch(reject);
    }),

  /**
   * Queue scraping for all profiles with a LinkedIn URL but no (or stub)
   * linkedin_info. Returns immediately with { queued: N }.
   */
  scrapeAllMissing: (onMessage: (msg: ScrapeEventMessage) => void): Promise<void> =>
    handleSseStream('/v1/linkedin/scrape/all-missing', onMessage),

  /**
   * Re-scrape ALL profiles with a LinkedIn URL, one at a time, overwriting
   * existing linkedin_info. Background task — returns immediately.
   */
  scrapeAllProfiles: (onMessage: (msg: ScrapeEventMessage) => void): Promise<void> =>
    handleSseStream('/v1/linkedin/scrape/all-profiles', onMessage),
};
